// Offline / dev-fixture sample data. Mirrors the design's `data.js` shape.
// Used only when VITE_DEV_FIXTURES=true. Real data comes from the API.

import type { AvatarColor, StatusColor } from "@/lib/tokens";

export interface SampleUser {
  id: string;
  display_name: string;
  initials: string;
  avatar_color: AvatarColor;
  email?: string;
}

export interface SampleWorkspace {
  id: string;
  name: string;
  slug: string;
  members: number;
  color: string;
}

export interface SampleBoardSummary {
  id: string;
  ws: string;
  name: string;
  description: string;
  updated: string;
  rows: number;
  members: string[];
}

export interface SampleStatusOption {
  id: string;
  label: string;
  color: StatusColor;
}

export type SampleColumnType =
  | "text"
  | "long_text"
  | "status"
  | "person"
  | "date"
  | "number"
  | "checkbox"
  | "tags"
  | "link"
  | "file";

export interface SampleColumn {
  id: string;
  type: SampleColumnType;
  name: string;
  width: number;
  options?: SampleStatusOption[];
  format?: "currency";
  currency?: string;
}

export interface SampleRow {
  id: string;
  title: string;
  status: string;
  owners: string[];
  due: string | null;
  budget: number;
  tags: string[];
  notes: string;
  link: { url: string; label: string } | null;
  done: boolean;
}

export interface SampleBoard {
  id: string;
  ws: string;
  name: string;
  description: string;
  columns: SampleColumn[];
  rows: SampleRow[];
}

export const sampleToday = "2026-05-19";

export const sampleMe: SampleUser = {
  id: "u1",
  display_name: "Alexander Knor",
  initials: "AK",
  avatar_color: "violet",
  email: "alexander.knor@logicline.eu",
};

export const sampleUsers: SampleUser[] = [
  sampleMe,
  { id: "u2", display_name: "Marton Bauer", initials: "MB", avatar_color: "blue" },
  { id: "u3", display_name: "Meikel Schäfer", initials: "MS", avatar_color: "amber" },
  { id: "u4", display_name: "Sarah Lehmann", initials: "SL", avatar_color: "green" },
  { id: "u5", display_name: "Jonas Weber", initials: "JW", avatar_color: "pink" },
  { id: "u6", display_name: "Lena Vogel", initials: "LV", avatar_color: "teal" },
  { id: "u7", display_name: "Tobias Krüger", initials: "TK", avatar_color: "slate" },
];

export const sampleWorkspaces: SampleWorkspace[] = [
  {
    id: "ws-ops",
    name: "LogicLine Operations",
    slug: "logicline-ops",
    members: 12,
    color: "#00599B",
  },
  { id: "ws-cs", name: "Kundenservice", slug: "kundenservice", members: 7, color: "#3D7FB8" },
  { id: "ws-tech", name: "Technik & Produktion", slug: "technik", members: 9, color: "#5E8B5F" },
  { id: "ws-mkt", name: "Marketing", slug: "marketing", members: 4, color: "#B45B7C" },
];

export const sampleBoards: SampleBoardSummary[] = [
  {
    id: "b1",
    ws: "ws-mkt",
    name: "Q2 Marketing-Kampagnen",
    description: "Aktive und geplante Kampagnen dieses Quartal",
    updated: "vor 2 Min.",
    rows: 14,
    members: ["u1", "u2", "u3", "u4"],
  },
  {
    id: "b2",
    ws: "ws-cs",
    name: "Kunden-Rückrufe",
    description: "Offene Tickets und Rückruf-Warteschlange",
    updated: "vor 14 Min.",
    rows: 38,
    members: ["u3", "u4", "u5"],
  },
  {
    id: "b3",
    ws: "ws-ops",
    name: "Onboarding-Pipeline",
    description: "Neue Mitarbeiter — erste 90 Tage",
    updated: "vor 1 Std.",
    rows: 9,
    members: ["u1", "u6"],
  },
  {
    id: "b4",
    ws: "ws-tech",
    name: "Außendienst — Tourenplan",
    description: "Servicerouten und Ausrüstung",
    updated: "Gestern",
    rows: 22,
    members: ["u3", "u7"],
  },
  {
    id: "b5",
    ws: "ws-ops",
    name: "Recruiting-Tracker",
    description: "Kandidaten im Prozess",
    updated: "Gestern",
    rows: 31,
    members: ["u1", "u4"],
  },
  {
    id: "b6",
    ws: "ws-tech",
    name: "Produktion — Q2 Backlog",
    description: "Aufträge, Planung, Blocker",
    updated: "vor 2 Tagen",
    rows: 47,
    members: ["u2", "u7", "u6"],
  },
];

export const sampleBoard: SampleBoard = {
  id: "b1",
  ws: "ws-mkt",
  name: "Q2 Marketing-Kampagnen",
  description: "Aktive und geplante Kampagnen dieses Quartal",
  columns: [
    { id: "c_title", type: "text", name: "Kampagne", width: 280 },
    {
      id: "c_status",
      type: "status",
      name: "Status",
      width: 138,
      options: [
        { id: "planning", label: "Planung", color: "slate" },
        { id: "in_progress", label: "In Bearbeitung", color: "blue" },
        { id: "review", label: "Prüfung", color: "amber" },
        { id: "blocked", label: "Blockiert", color: "red" },
        { id: "done", label: "Erledigt", color: "green" },
        { id: "on_hold", label: "Pausiert", color: "gray" },
      ],
    },
    { id: "c_owner", type: "person", name: "Verantwortlich", width: 148 },
    { id: "c_due", type: "date", name: "Fällig", width: 124 },
    {
      id: "c_budget",
      type: "number",
      name: "Budget",
      width: 116,
      format: "currency",
      currency: "EUR",
    },
    { id: "c_tags", type: "tags", name: "Zielgruppe", width: 196 },
    { id: "c_notes", type: "long_text", name: "Notizen", width: 280 },
    { id: "c_brief", type: "link", name: "Briefing", width: 132 },
    { id: "c_done", type: "checkbox", name: "Geprüft", width: 96 },
  ],
  rows: [
    {
      id: "r1",
      title: "Produkt-Launch — Landingpage Frühjahr",
      status: "in_progress",
      owners: ["u2"],
      due: "2026-05-28",
      budget: 12000,
      tags: ["B2B", "DACH"],
      notes: "Hero-Copy von Agentur ausstehend. Visuals freigegeben.",
      link: { url: "https://notion.so/spring-launch", label: "Notion-Brief" },
      done: false,
    },
    {
      id: "r2",
      title: "Referenzvideo — ACME München",
      status: "review",
      owners: ["u4"],
      due: "2026-05-21",
      budget: 4500,
      tags: ["Video", "Case Study"],
      notes: "Finaler Schnitt beim Kunden zur Freigabe.",
      link: { url: "https://drive.example.com/acme", label: "Drehplan" },
      done: false,
    },
    {
      id: "r3",
      title: "Messeauftritt — Hannover Messe Stand",
      status: "blocked",
      owners: ["u1", "u3"],
      due: "2026-04-30",
      budget: 38000,
      tags: ["Events", "B2B"],
      notes: "Standlieferung um 2 Wochen verzögert. Fallback benötigt.",
      link: { url: "https://hannover-messe.de", label: "Event-Seite" },
      done: false,
    },
    {
      id: "r4",
      title: "Quartals-Newsletter — Mai-Ausgabe",
      status: "done",
      owners: ["u5"],
      due: "2026-05-10",
      budget: 0,
      tags: ["E-Mail", "Newsletter"],
      notes: "Versendet 10.05.2026 — 34 % Öffnungsrate, 7 % Klicks.",
      link: { url: "https://example.com/newsletter-may", label: "Public Copy" },
      done: true,
    },
    {
      id: "r5",
      title: "LinkedIn-Paid — Q2 Lead-Gen",
      status: "planning",
      owners: ["u2", "u4"],
      due: "2026-06-15",
      budget: 18000,
      tags: ["Paid", "LinkedIn", "Lead-Gen"],
      notes: "Zielgruppe nächste Woche mit Vertrieb finalisieren.",
      link: null,
      done: false,
    },
    {
      id: "r6",
      title: "Website-Navigation Redesign",
      status: "on_hold",
      owners: ["u3"],
      due: null,
      budget: 0,
      tags: ["Website"],
      notes: "Pausiert bis Q3 — abhängig von Produkt-Naming.",
      link: null,
      done: false,
    },
    {
      id: "r7",
      title: "Partner-Co-Marketing — Siemens",
      status: "in_progress",
      owners: ["u1", "u6"],
      due: "2026-06-02",
      budget: 8000,
      tags: ["Partner", "B2B"],
      notes: "Outline geteilt. Co-Branding-Freigabe von Legal ausstehend.",
      link: { url: "https://drive.example.com/siemens", label: "Outline v3" },
      done: false,
    },
    {
      id: "r8",
      title: "Sales-Enablement Deck",
      status: "review",
      owners: ["u4", "u5"],
      due: "2026-05-23",
      budget: 1200,
      tags: ["Intern", "Vertrieb"],
      notes: "Von 42 auf 18 Folien reduziert. Sales-Lead prüft.",
      link: { url: "https://example.com/deck", label: "v4 Entwurf" },
      done: false,
    },
    {
      id: "r9",
      title: "Produktfotos — neue SKUs",
      status: "planning",
      owners: ["u6"],
      due: "2026-06-10",
      budget: 6000,
      tags: ["Content", "Produkt"],
      notes: "Studio gebucht für KW 24. SKU-Liste finalisieren (12 Stk.).",
      link: null,
      done: false,
    },
    {
      id: "r10",
      title: 'Webinar-Reihe — „Operations 2026"',
      status: "done",
      owners: ["u2"],
      due: "2026-04-22",
      budget: 2400,
      tags: ["Webinar", "B2B"],
      notes: "3-teilige Reihe. Ø 84 Live-Teilnehmer. Aufzeichnungen auf YouTube.",
      link: { url: "https://example.com/webinar", label: "Recap" },
      done: true,
    },
  ],
};

export const sampleLiveViewers = ["u1", "u2", "u6"];

export function sampleUserById(id: string): SampleUser | undefined {
  return sampleUsers.find((u) => u.id === id);
}

export function devFixturesEnabled(): boolean {
  return import.meta.env.VITE_DEV_FIXTURES === "true";
}
